import { PaymentSelectState } from '@/features/paymentSelect/paymentSelectSlice';
import { TransactionState } from '@/features/paymentProcess/transactionSlice';
import { journeyPathPrefixes } from './utils';

export interface DetectStaleJourneyStateInput {
  pathname: string,
  paymentSelect: PaymentSelectState,
  transaction: TransactionState,
}

export interface DetectStaleJourneyStateResult {
  isStale: boolean,
  reason: string,
}

export interface StaleCompletedJourneyCheck {
  pathname: string,
  transactionComplete: boolean,
  transactionIdPresent: boolean,
  subscriptionIdPresent: boolean,
}

function getOrderReference(paymentSelect: PaymentSelectState): string {
  // Starts by checking if we've got an order reference in the payment select state
  if (paymentSelect.orderReference) {
    return paymentSelect.orderReference;
  }

  if (typeof window === 'undefined') {
    return '';
  }
  // Checks if we've got payment select in session storage
  // (via redux-remember library) which is an indicator that
  // the user has started a journey, so long as we then find
  // an order reference within it
  const paymentSelectStoredInSessionStorage = window.sessionStorage.getItem('@@remember-paymentSelect');

  if (!paymentSelectStoredInSessionStorage) {
    return '';
  }

  try {
    const persisted = JSON.parse(paymentSelectStoredInSessionStorage) as { orderReference?: string };
    return persisted.orderReference ?? '';
  } catch {
    return '';
  }
}

function getTransactionFields(transaction: TransactionState) {
  let persistedComplete: boolean | undefined;
  let persistedTransactionId: string | undefined;
  let persistedSubscriptionId: string | undefined;

  if (typeof window !== 'undefined') {
    // Looking for a transactionId or subscriptionId is a good indicator
    // that the user has completed a journey
    const transactionStoredInSessionStorage = window.sessionStorage.getItem('@@remember-transaction');

    if (transactionStoredInSessionStorage) {
      try {
        const persisted = JSON.parse(transactionStoredInSessionStorage) as {
          complete?: boolean,
          transactionId?: string,
          subscriptionId?: string,
        };
        persistedComplete = persisted.complete;
        persistedTransactionId = persisted.transactionId;
        persistedSubscriptionId = persisted.subscriptionId;
      } catch {
        // Ignore invalid persisted state
      }
    }
  }

  return {
    transactionComplete: Boolean(transaction.complete || persistedComplete),
    transactionIdPresent: Boolean(transaction.transactionId || persistedTransactionId),
    subscriptionIdPresent: Boolean(transaction.subscriptionId || persistedSubscriptionId),
  };
}

export function isMidJourneyRoute(pathname: string): boolean {
  if (pathname === '/') {
    // Home page, journey hasn't started yet
    return false;
  }

  return journeyPathPrefixes.some(
    basePath => pathname.startsWith(basePath) || pathname === basePath.slice(0, -1)
  );
}

export function isJourneySuccessOrSubmittedPath(pathname: string): boolean {
  return isMidJourneyRoute(pathname)
    && (pathname.endsWith('/success') || pathname.endsWith('/submitted'));
}

export function isLikelyStaleCompletedJourneyState({
  pathname,
  transactionComplete,
  transactionIdPresent,
  subscriptionIdPresent,
}: StaleCompletedJourneyCheck): boolean {
  return isJourneySuccessOrSubmittedPath(pathname)
    && !transactionComplete
    && !transactionIdPresent
    && !subscriptionIdPresent;
}

export function detectStaleJourneyState({
  pathname,
  paymentSelect,
  transaction,
}: DetectStaleJourneyStateInput): DetectStaleJourneyStateResult {
  // First we check if the route is a mid-journey route, if not it's
  // not a stale journey
  if (!isMidJourneyRoute(pathname)) {
    return { isStale: false, reason: 'not_a_journey_route' };
  }

  const {
    transactionComplete,
    transactionIdPresent,
    subscriptionIdPresent,
  } = getTransactionFields(transaction);

  // We then check if the journey is likely to be a stale completed one
  if (isLikelyStaleCompletedJourneyState({
    pathname,
    transactionComplete,
    transactionIdPresent,
    subscriptionIdPresent,
  })) {
    return { isStale: true, reason: 'empty_transaction_on_success_page' };
  }

  // Then we check if it's mid-journey owing to the presence of an order reference
  if (!getOrderReference(paymentSelect)) {
    return { isStale: true, reason: 'missing_order_reference_on_journey_route' };
  }

  return { isStale: false, reason: 'valid_session' };
}
